import { useEffect, useMemo, useState } from 'react';
import { pf2eApi, type PF2eTrait } from '@/api/client';
import { useLocalization } from '@/i18n';
import { createWizardLogger } from '@/utils/wizardLogger';

interface TraitLookupState {
  loading: boolean;
  trait: PF2eTrait | null;
  error: boolean;
}

const cache = new Map<string, TraitLookupState>();
const listeners = new Map<string, Set<(state: TraitLookupState) => void>>();
const pending = new Map<string, Map<string, string>>();
const timers = new Map<string, number>();
const traitLogger = createWizardLogger('encounter.traits');

function cacheKey(locale: string, label: string): string {
  return `${locale}:${label.trim().toLocaleLowerCase()}`;
}

function publish(key: string, state: TraitLookupState) {
  cache.set(key, state);
  listeners.get(key)?.forEach(listener => listener(state));
}

async function flush(locale: string) {
  timers.delete(locale);
  const labels = [...(pending.get(locale)?.values() || [])];
  pending.delete(locale);
  for (let index = 0; index < labels.length; index += 200) {
    const batch = labels.slice(index, index + 200);
    try {
      const response = await pf2eApi.getTraitsBatch(batch, locale);
      for (const label of batch) {
        publish(cacheKey(locale, label), {
          loading: false,
          trait: response.data.entries[label] || null,
          error: false,
        });
      }
    } catch {
      traitLogger.warn('Trait batch load failed', {
        locale,
        requested_count: batch.length,
      });
      for (const label of batch) {
        publish(cacheKey(locale, label), { loading: false, trait: null, error: true });
      }
    }
  }
}

function enqueue(locale: string, label: string) {
  const key = cacheKey(locale, label);
  if (cache.has(key)) return;
  publish(key, { loading: true, trait: null, error: false });
  const localePending = pending.get(locale) || new Map<string, string>();
  localePending.set(key, label.trim());
  pending.set(locale, localePending);
  if (!timers.has(locale)) {
    timers.set(locale, window.setTimeout(() => void flush(locale), 20));
  }
}

export function useTraitDefinition(label: string | null | undefined): TraitLookupState {
  const { activeLanguage, requestedLanguage } = useLocalization();
  const locale = activeLanguage || requestedLanguage || 'en';
  const normalizedLabel = String(label || '').trim();
  const key = useMemo(
    () => cacheKey(locale, normalizedLabel),
    [locale, normalizedLabel],
  );
  const [state, setState] = useState<TraitLookupState>(
    () => cache.get(key) || { loading: Boolean(normalizedLabel), trait: null, error: false },
  );

  useEffect(() => {
    if (!normalizedLabel) {
      setState({ loading: false, trait: null, error: false });
      return;
    }
    const keyListeners = listeners.get(key) || new Set();
    keyListeners.add(setState);
    listeners.set(key, keyListeners);
    setState(cache.get(key) || { loading: true, trait: null, error: false });
    enqueue(locale, normalizedLabel);
    return () => {
      keyListeners.delete(setState);
      if (keyListeners.size === 0) listeners.delete(key);
    };
  }, [key, locale, normalizedLabel]);

  return state;
}
